<template>
    <div class="row no-wrap items-stretch">
        <div class="flex items-center">
            <q-btn
                flat
                class="full-height"
                icon="fa-solid fa-chevron-left"
                color="secondary"
                @click="$emit('previous')"
            >
                <q-tooltip>{{ $t('Previous period') }}</q-tooltip>
            </q-btn>
        </div>
        <div class="col row no-wrap q-col-gutter-md items-stretch">
            <div
                class="col row items-center justify-center"
                style="width: 100%"
            >
                <div
                    v-for="(run, idx) in reversedProductRuns"
                    :key="idx"
                    class="col-auto"
                    :style="getChartStyle(run)"
                >
                    <div v-if="$q.screen.gt.xs" class="product-run-title">
                        <q-badge
                            v-if="run.product_name"
                            class="text-bold ellipsis q-mt-sm"
                            color="white"
                            text-color="grey-6"
                        >
                            {{ run.product_name }}
                            <q-tooltip class="bg-white shadow-3">
                                <template v-slot:default>
                                    <div
                                        class="text-grey-8 q-pa-xs tooltip-content q-gutter-sm"
                                    >
                                        <div class="text-weight-bold">
                                            <span
                                                class="tooltip-color-marker"
                                                :style="{
                                                    backgroundColor:
                                                        positiveColor
                                                }"
                                                aria-hidden="true"
                                            ></span>
                                            <span class="ellipsis">{{
                                                run.product_name
                                            }}</span>
                                        </div>
                                        <div>
                                            {{ formatRunStartDate(run.start) }}
                                            ({{ getRunDurationFormatted(run) }})
                                        </div>
                                        <div
                                            v-if="
                                                run.product_count !== null &&
                                                run.product_count !== undefined
                                            "
                                        >
                                            {{
                                                formatProductionValue(
                                                    run.product_count,
                                                    userLocale
                                                )
                                            }}
                                            ({{
                                                $t('{unitsPerHour} u/h', {
                                                    unitsPerHour:
                                                        getUnitsPerHour(run)
                                                })
                                            }})
                                        </div>
                                        <div
                                            v-if="
                                                formatProductQuantity(
                                                    run,
                                                    facilityUnitSystem,
                                                    userLocale
                                                )
                                            "
                                        >
                                            {{
                                                formatProductQuantity(
                                                    run,
                                                    facilityUnitSystem,
                                                    userLocale
                                                )
                                            }}
                                        </div>
                                    </div>
                                </template>
                            </q-tooltip>
                        </q-badge>
                        <div
                            v-if="run.phases[0]['phase'] === 'stopped'"
                            class="shutdown-run"
                        ></div>
                    </div>
                    <div v-else style="margin-block: 28px">
                        <!-- Empty div to align the buttons and the phases graph -->
                    </div>
                    <phases-graph
                        :phases="run.phases"
                        :start-date="startDate"
                        :end-date="endDate"
                        :graph-period-in-hours="graphPeriodInHours"
                        @phaseClick="onPhaseClick"
                    />
                </div>
            </div>
        </div>
        <div class="flex items-center">
            <q-btn
                flat
                class="full-height"
                icon="fa-solid fa-chevron-right"
                color="secondary"
                @click="$emit('next')"
                :disabled="isCurrentPeriod"
            >
                <q-tooltip v-if="!isCurrentPeriod">{{
                    $t('Next period')
                }}</q-tooltip>
            </q-btn>
        </div>
    </div>
</template>

<script>
import PhasesGraph from '@components/PhasesGraph.vue';
import { convertDurationToDHMS } from '@utils/time.js';
import { formatProductionValue } from '@utils/numberFormatters.js';
import { convertToUnitSystem } from '@models/products.js';
import { useAuthStore } from '@stores/auth';
import { mapState } from 'pinia';
import { colors } from 'quasar';
import { date } from 'quasar';

export default {
    name: 'ProductRunsGraph',
    components: { PhasesGraph },
    emits: ['previous', 'next', 'phaseClick'],
    props: {
        /**
         * List of product runs returned by the API.
         * @type {Array}
         */
        productRuns: {
            type: Array,
            required: true
        },
        /**
         * ISO string representing the window start (12-h before end).
         * @type {String}
         */
        startDate: {
            type: String,
            required: true
        },
        /**
         * ISO string representing the window end (datetimePickerEndDate).
         * @type {String}
         */
        endDate: {
            type: String,
            required: true
        },
        /**
         * Period displayed by every chart, in hours. Defaults to 12.
         * @type {Number}
         */
        graphPeriodInHours: {
            type: Number,
            default: 12
        },
        /**
         * Facility unit system.
         * @type {String}
         */
        facilityUnitSystem: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            // used to calculate the duration of the run on the tooltip
            now: new Date()
        };
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Positive (success) palette color for marker.
         * @returns {string}
         */
        positiveColor() {
            return colors.getPaletteColor('positive');
        },
        /**
         * Product runs in reverse chronological order (oldest to newest).
         * @returns {Array} Reversed product runs array.
         */
        reversedProductRuns() {
            return this.productRuns.slice().reverse();
        },
        /**
         * Whether the current period is selected.
         * @returns {boolean} True if the current period is selected.
         */
        isCurrentPeriod() {
            const now = new Date();
            const endDate = new Date(this.endDate);
            const diff = date.getDateDiff(now, endDate, 'hours');
            return diff < this.graphPeriodInHours;
        }
    },
    methods: {
        convertDurationToDHMS,
        formatProductionValue,
        /**
         * Format the run start date for display in tooltip.
         * @param {string} startISO
         * @returns {string}
         */
        formatRunStartDate(startISO) {
            const start = new Date(startISO);
            return date.formatDate(start, 'MM/DD HH:mm:ss');
        },
        /**
         * Get formatted duration for a run.
         * @param {Object} run
         * @returns {string}
         */
        getRunDurationFormatted(run) {
            const start = new Date(run.start);
            const end = run.end ? new Date(run.end) : this.now;
            const duration = date.getDateDiff(end, start, 'seconds');
            return this.convertDurationToDHMS(duration);
        },
        /**
         * Calculate clamped duration (in seconds) of a product run inside the
         * [startDate, endDate] time window.
         *
         * @param {Object} run - Product run object.
         * @returns {number} Clamped duration in seconds.
         */
        getClampedDuration(run) {
            const windowStart = new Date(this.startDate);
            const windowEnd = new Date(this.endDate);

            let start = new Date(run.start);
            let end = run.end ? new Date(run.end) : windowEnd;

            if (start < windowStart) start = windowStart;
            if (end > windowEnd) end = windowEnd;

            const diffMs = end - start;
            return diffMs > 0 ? diffMs / 1000 : 0;
        },
        /**
         * Build the flex-box style so that each chart width is proportional to
         * its duration within the 12-hour window.
         *
         * @param {Object} run - Product run object.
         * @returns {Object} Inline style object.
         */
        getChartStyle(run) {
            const seconds = this.getClampedDuration(run);
            const percent = (seconds / (this.graphPeriodInHours * 3600)) * 100;
            return {
                flex: `0 0 ${percent}%`,
                maxWidth: `${percent}%`
            };
        },
        /**
         * Calculate units per hour for the label.
         *
         * @param {Object} run - Product run object.
         * @returns {number} Units per hour rounded.
         */
        getUnitsPerHour(run) {
            if (!run.duration_s || run.duration_s === 0) return 0;
            const unitsPerHour = Math.round(
                (run.product_count || 0) / (run.duration_s / 3600)
            );
            return this.formatProductionValue(unitsPerHour, this.userLocale);
        },
        /**
         * Format the product quantity.
         *
         * @param {Object} run - Product run object.
         * @param {String} unitSystem - Unit system.
         * @param {String} locale - Locale.
         * @returns {String} Formatted product quantity.
         */
        formatProductQuantity(run, unitSystem, locale) {
            if (run.product_quantity === null) {
                return null;
            }

            const { value: converted_value, unit: converted_unit } =
                convertToUnitSystem(
                    parseFloat(run.product_quantity),
                    run.unit_measure,
                    unitSystem
                );

            const formattedValue = this.formatProductionValue(
                converted_value,
                locale
            );

            return this.$t('{value} {unit}', {
                value: formattedValue,
                unit: converted_unit
            });
        },
        /**
         * Handle phase click events and emit them to parent component.
         * @param {Object} phase - The clicked phase data
         */
        onPhaseClick(phase) {
            this.$emit('phaseClick', phase);
        }
    }
};
</script>

<style lang="scss" scoped>
.product-run-title {
    text-align: center;
    overflow: hidden;
}

.tooltip-content {
    font-size: 14px;
}

.tooltip-color-marker {
    height: 10px;
    width: 10px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 6px;
    vertical-align: middle;
}

// Align the shutdown run with the product runs
.shutdown-run {
    margin-top: 29px !important;
}
</style>
